/**
 * InvoiceViewDialog — the single, shared way to VIEW an invoice.
 *
 * It renders the exact same server page that gets shared (`/api/shared/invoice/...`)
 * inside an in-app iframe, so View and Share can never drift. Uses the `preview=1`
 * URL so a vendor preview doesn't inflate the sent/seen counters, and lives in a
 * MUI Dialog with its own close button — no window.open, so no pop-up blocker.
 *
 * Props:
 *   businessId {number}
 *   invoiceId  {number|null}  – truthy opens the dialog; null/undefined closes it
 *   onClose    {function}     – () => void
 */

import React, { useEffect, useState } from 'react';
import { APP_DIALOG, APP_DIALOG_TITLE_SX } from '../utils/dialogStyle';
import { Dialog, Typography, IconButton, Box, CircularProgress } from '@mui/material';
import { Close } from '@mui/icons-material';
import { invoiceAPI } from '../api';

export default function InvoiceViewDialog({ businessId, invoiceId, onClose }) {
    const [url, setUrl] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!invoiceId) { setUrl(null); setError(false); return; }
        let cancelled = false;
        setUrl(null);
        setError(false);
        (async () => {
            try {
                const res = await invoiceAPI.getInvoiceShareUrl(businessId, invoiceId, { preview: true });
                let shareUrl = res.data?.share_url || null;
                // Prod serves the app over https and the API on the same host. If the
                // backend built an http:// URL (e.g. nginx didn't forward the scheme),
                // embedding it in the https page is blocked as mixed content and the
                // iframe stays blank. Upgrade the scheme to match the app's.
                if (shareUrl && window.location.protocol === 'https:' && shareUrl.startsWith('http://')) {
                    shareUrl = 'https://' + shareUrl.slice('http://'.length);
                }
                if (!cancelled) setUrl(shareUrl);
            } catch (_) {
                if (!cancelled) setError(true);
            }
        })();
        return () => { cancelled = true; };
    }, [businessId, invoiceId]);

    return (
        // Full screen on a phone, a centred column on a desktop — like every other full-page
        // dialog — so the title and ✕ sit right above the bill instead of at the window's edges.
        <Dialog open={!!invoiceId} onClose={onClose} {...APP_DIALOG}
            PaperProps={{ sx: { ...APP_DIALOG.PaperProps.sx, height: { xs: '100%', sm: 'calc(100% - 64px)' } } }}>
            {/* The app's dialog header (it keeps the close button below the notch in the PWA) */}
            <Box sx={{ ...APP_DIALOG_TITLE_SX, px: 2, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <Typography variant="h6" sx={{ flex: 1, fontWeight: 'bold' }}>Invoice</Typography>
                <IconButton onClick={onClose} aria-label="Close"><Close /></IconButton>
            </Box>
            {/* pb: keep the bill above the home indicator */}
            <Box sx={{ flex: 1, minHeight: 0, bgcolor: 'grey.100', display: 'flex', pb: 'env(safe-area-inset-bottom, 0px)' }}>
                {error ? (
                    <Box sx={{ m: 'auto', p: 3, textAlign: 'center' }}>
                        <Typography color="error">Could not load the invoice. Please try again.</Typography>
                    </Box>
                ) : !url ? (
                    <Box sx={{ m: 'auto', p: 3, textAlign: 'center' }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <iframe
                        title="Invoice"
                        src={url}
                        style={{ flex: 1, width: '100%', height: '100%', border: 0 }}
                    />
                )}
            </Box>
        </Dialog>
    );
}
